import { useEffect, useRef, useState } from 'react';
import { Badge, Callout, Card, DefinitionList, Field, SectionHeading, TextInput } from '@/components/ui';
import { appendAudit } from '@/db/audit';
import { recordBackupTimestamp } from '@/db/repositories';
import { formatDateTime, nowIso } from '@/domain/dates';
import { buildExportDataset } from '@/export/dataset';
import { downloadBlob } from '@/export/zip';
import { useSession } from '@/session/SessionContext';
import { useT } from '@/i18n';
import {
  assessBackup,
  formatBytes,
  formatLastBackup,
  requestPersistentStorage,
  storageEstimate,
} from './backupStatus';
import {
  backupFileName,
  createBackupFile,
  openBackupPayload,
  parseBackupFile,
  restoreBackup,
  type BackupFile,
  type BackupHeader,
} from './backupFile';

export function BackupScreen() {
  const session = useSession();
  const t = useT();
  const status = assessBackup(session.app);

  const [passphrase, setPassphrase] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [storage, setStorage] = useState<Awaited<ReturnType<typeof storageEstimate>>>(null);
  const [persisted, setPersisted] = useState<boolean | null>(null);

  useEffect(() => {
    void (async () => {
      setStorage(await storageEstimate());
      setPersisted((await navigator.storage?.persisted?.()) ?? null);
    })();
  }, []);

  async function handleBackup() {
    setError(null);
    setSuccess(null);

    if (passphrase.length < 12) {
      setError(t.backup.errShort);
      return;
    }
    if (passphrase !== confirmation) {
      setError(t.backup.errMismatch);
      return;
    }

    setBusy('backup');
    try {
      const { blob, header } = await createBackupFile({
        dataKey: session.dataKey,
        passphrase,
        label: label.trim(),
        accountId: session.account.id,
      });
      downloadBlob(blob, backupFileName(header.createdAt));

      // The structured archive goes alongside the encrypted file. If the app
      // is ever gone, a plain ZIP of CSVs is still readable; an encrypted
      // container needs this app to open it.
      const dataset = await buildExportDataset({
        dataKey: session.dataKey,
        from: '2000-01-01',
        to: '2099-12-31',
        company: session.company,
        app: session.app,
        driver: session.account,
        passengers: session.passengers,
        addresses: session.addresses,
        vehicles: session.vehicles,
      });
      const { renderGobdArchive } = await import('@/export/gobd');
      downloadBlob(
        renderGobdArchive(dataset),
        `GoBD_Gesamtarchiv_${header.createdAt.slice(0, 10)}.zip`,
      );

      await recordBackupTimestamp(session.audit, nowIso());
      await session.refresh();

      setPassphrase('');
      setConfirmation('');
      setSuccess(
        t.backup.done(
          header.summary.trips,
          header.summary.invoices,
          header.summary.auditEntries,
        ),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.backup.failed);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-5">
      <SectionHeading title={t.backup.title} description={t.backup.description} />

      <Callout tone={status.severity === 'ok' ? 'success' : status.severity === 'overdue' ? 'danger' : 'warning'}>
        {status.message(t)}
      </Callout>

      <Card title={t.backup.whyTitle}>
        <div className="text-muted space-y-2 text-sm leading-relaxed">
          <p>{t.backup.why1}</p>
          <p>
            {t.backup.why2(
              session.app.backupWarnAfterDays,
              session.app.backupBlockAfterDays,
            )}
          </p>
          <p>{t.backup.why3}</p>
        </div>
      </Card>

      <Card title={t.backup.storageTitle}>
        <DefinitionList
          items={[
            { term: t.backup.lastBackup, value: formatLastBackup(session.app, t) },
            {
              term: t.backup.usedStorage,
              value: storage
                ? t.backup.usedOf(
                    formatBytes(storage.usedBytes),
                    formatBytes(storage.quotaBytes),
                    storage.percentUsed.toFixed(1),
                  )
                : t.backup.notAvailable,
            },
            {
              term: t.backup.protected,
              value:
                persisted === null ? (
                  t.backup.unknown
                ) : persisted ? (
                  <Badge tone="success">{t.backup.yes}</Badge>
                ) : (
                  <Badge tone="warning">{t.backup.no}</Badge>
                ),
            },
          ]}
        />
        {persisted === false && (
          <button
            type="button"
            className="btn-secondary mt-3"
            onClick={() => void requestPersistentStorage().then(setPersisted)}
          >
            {t.backup.requestPersistent}
          </button>
        )}
      </Card>

      <Card title={t.backup.createTitle}>
        <p className="text-muted mb-4 text-sm leading-relaxed">{t.backup.createBody}</p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t.backup.passphrase} required hint={t.backup.passphraseHint}>
            <TextInput
              type="password"
              value={passphrase}
              onChange={(event) => setPassphrase(event.target.value)}
              autoComplete="new-password"
            />
          </Field>
          <Field label={t.backup.passphraseRepeat} required>
            <TextInput
              type="password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="new-password"
            />
          </Field>
        </div>

        <div className="mt-4">
          <Field label={t.backup.label} hint={t.backup.labelHint}>
            <TextInput
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder={t.backup.labelPlaceholder}
            />
          </Field>
        </div>

        {error && (
          <div className="mt-4">
            <Callout tone="danger">{error}</Callout>
          </div>
        )}
        {success && (
          <div className="mt-4">
            <Callout tone="success">{success}</Callout>
          </div>
        )}

        <button
          type="button"
          className="btn-primary mt-4"
          onClick={() => void handleBackup()}
          disabled={busy !== null}
        >
          {busy === 'backup' ? t.backup.creating : t.backup.create}
        </button>
      </Card>

      <RestorePanel />
    </div>
  );
}

function RestorePanel() {
  const session = useSession();
  const t = useT();
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<BackupFile | null>(null);
  const [header, setHeader] = useState<BackupHeader | null>(null);
  const [passphrase, setPassphrase] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  async function handleFile(selected: File) {
    setError(null);
    setResult(null);
    try {
      const parsed = await parseBackupFile(await selected.text());
      setFile(parsed);
      setHeader(parsed.header);
    } catch (cause) {
      setFile(null);
      setHeader(null);
      setError(cause instanceof Error ? cause.message : t.backup.fileUnreadable);
    }
  }

  async function handleRestore() {
    if (!file) return;
    // The typed word stays German in both languages: it is compared against a
    // constant, and translating it would make an English-language backup
    // unrestorable from a German-language device.
    if (confirmText.trim().toUpperCase() !== 'WIEDERHERSTELLEN') {
      setError(t.backup.errConfirmation);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const payload = await openBackupPayload(file, passphrase);
      const outcome = await restoreBackup(payload, session.dataKey);

      await appendAudit(session.audit, {
        action: 'restore',
        entityType: 'system',
        entityId: 'restore',
        entityLabel: `Wiederherstellung aus Sicherung vom ${formatDateTime(file.header.createdAt)}`,
        changes: [],
        reason: file.header.label || undefined,
      });

      await session.refresh();

      setResult(
        t.backup.restored(
          outcome.restored.trips,
          outcome.restored.invoices,
          outcome.restored.auditEntries,
          outcome.chainAfterRestore.ok
            ? t.backup.chainUnchanged
            : t.backup.chainProblems(outcome.chainAfterRestore.problems.length),
        ),
      );
      setFile(null);
      setHeader(null);
      setPassphrase('');
      setConfirmText('');
      if (fileInput.current) fileInput.current.value = '';
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.backup.restoreFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t.backup.restoreTitle}>
      <Callout tone="warning">{t.backup.restoreWarning}</Callout>

      <div className="mt-4">
        <Field label={t.backup.file}>
          <input
            ref={fileInput}
            type="file"
            accept=".fbbak,application/json"
            className="field-input file:bg-subtle file:text-body file:mr-3 file:rounded file:border-0 file:px-3 file:py-1 file:text-sm"
            onChange={(event) => {
              const selected = event.target.files?.[0];
              if (selected) void handleFile(selected);
            }}
          />
        </Field>
      </div>

      {header && (
        <div className="mt-4 space-y-4">
          <DefinitionList
            items={[
              { term: t.backup.createdAt, value: formatDateTime(header.createdAt) },
              { term: t.backup.label, value: header.label || t.common.dash },
              {
                term: t.backup.contents,
                value: t.backup.contentsValue(
                  header.summary.trips,
                  header.summary.shifts,
                  header.summary.invoices,
                  header.summary.auditEntries,
                ),
              },
              {
                term: t.backup.chainAtBackup,
                value: header.chainOk ? (
                  <Badge tone="success">{t.backup.chainOk}</Badge>
                ) : (
                  <Badge tone="danger">{t.backup.chainBad}</Badge>
                ),
              },
            ]}
          />

          <Field label={t.backup.filePassphrase} required>
            <TextInput
              type="password"
              value={passphrase}
              onChange={(event) => setPassphrase(event.target.value)}
              autoComplete="off"
            />
          </Field>

          <Field label={t.backup.confirmation} required hint={t.backup.confirmationHint}>
            <TextInput
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              autoCapitalize="characters"
            />
          </Field>

          <button
            type="button"
            className="btn-danger"
            onClick={() => void handleRestore()}
            disabled={busy || !passphrase}
          >
            {busy ? t.backup.restoring : t.backup.replaceAll}
          </button>
        </div>
      )}

      {error && (
        <div className="mt-4">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}
      {result && (
        <div className="mt-4">
          <Callout tone="success">{result}</Callout>
        </div>
      )}
    </Card>
  );
}
